import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { getPerms } from '@/lib/perms';
import { Icons } from '@/components/icons';
import { useQueryClient } from '@tanstack/react-query';
import { cloneDeep } from 'es-toolkit/compat';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { toHandleWidgetData } from '../data/to-handle';
import { DASHBOARD_QUERY_KEY } from '../hooks/use-get-dashboard';
import { useUpdateDashboard } from '../hooks/use-update-dashboard';
import type { DashBoard, Widget, WorkEngagementDashboard } from '../types';

const filteredWidgetsOnPermissions = (widgets: Widget[]) => {
  return widgets.filter((widget) => {
    const modelNames = widget.modelNames;
    if (!modelNames) return false;

    // Check if ANY model has viewing permissions
    return modelNames.some((modelName) => {
      const perms = getPerms(modelName);
      return perms.canView;
    });
  });
};

export function EditTodoWidgetModal({
  dashboard,
  closeFunction,
}: {
  dashboard: DashBoard | WorkEngagementDashboard;
  closeFunction: VoidFunction;
}) {
  const filteredWidgets = useMemo(
    () =>
      filteredWidgetsOnPermissions(toHandleWidgetData).filter(
        (widget) => widget.category === 'to_handle'
      ),
    []
  );
  const queryClient = useQueryClient();
  const { mutateAsync } = useUpdateDashboard();
  const [selected, setSelected] = useState<Map<string, Set<string>>>(() => {
    const newSelected = new Map();
    for (const widget of toHandleWidgetData) {
      const newSet = new Set();
      for (const stat of widget.statistics) {
        if (
          dashboard.widgets.find(
            (w) =>
              w.category === widget.category &&
              w.sub_category === widget.sub_category &&
              w.statistics.find((s) => s.identifier === stat.identifier)
          )
        ) {
          newSet.add(stat.identifier);
        }
      }
      newSelected.set(widget.sub_category, newSet);
    }
    return newSelected;
  });
  const onSubmit = () => {
    const data = toHandleWidgetData
      .filter((widget) => (selected.get(widget.sub_category)?.size ?? 0) > 0)
      .map((widget) => ({
        ...widget,
        statistics: widget.statistics.filter((stat) => {
          return selected.get(widget.sub_category)?.has(stat.identifier);
        }),
      }));
    const clonedDashboard = cloneDeep(dashboard);
    const allOtherWidgets = clonedDashboard.widgets.filter(
      (widget) => widget.category !== 'to_handle'
    );

    const newWidgets = [...allOtherWidgets, ...data];
    clonedDashboard.widgets = newWidgets;
    toast.promise(mutateAsync(clonedDashboard), {
      loading: 'Sparar...',
      success: () => {
        closeFunction();
        queryClient.invalidateQueries({
          queryKey: [DASHBOARD_QUERY_KEY],
        });
        return 'Sparad';
      },
      error: () => {
        return 'Misslyckades med att spara';
      },
    });
  };

  const defaultOpen = [...selected]
    .filter(([_, categories]) => categories.size > 0)
    .map(([category]) => category);
  return (
    <Dialog defaultOpen onOpenChange={closeFunction}>
      <DialogContent className='gap-0 p-0'>
        <DialogHeader>
          <DialogTitle>Redigera Att hantera</DialogTitle>
          <Button variant='outline' size='icon-sm' onClick={closeFunction}>
            <span className='sr-only'>Stäng</span>
            <Icons.close className='size-4' />
          </Button>
          <DialogDescription className='sr-only'>
            Redigera att hantera
          </DialogDescription>
        </DialogHeader>
        <div className='h-96 overflow-y-auto p-4'>
          <Accordion
            type='multiple'
            className='grid gap-2'
            defaultValue={defaultOpen}
          >
            {filteredWidgets.map((widget) => (
              <AccordionItem
                key={widget.sub_category}
                value={widget.sub_category}
                className='rounded-md border p-2 [&[data-state=open]>svg]:rotate-180'
              >
                <div className='flex items-center gap-2'>
                  <Checkbox
                    checked={
                      selected.get(widget.sub_category)?.size ===
                      widget.statistics.length
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelected((prev) => {
                          const newMap = new Map(prev);
                          newMap.set(
                            widget.sub_category,
                            new Set(
                              widget.statistics.map((stat) => stat.identifier)
                            )
                          );
                          return newMap;
                        });
                      } else {
                        setSelected((prev) => {
                          const newMap = new Map(prev);
                          newMap.set(widget.sub_category, new Set());
                          return newMap;
                        });
                      }
                    }}
                  />
                  <AccordionTrigger
                    icon={false}
                    className='flex items-center justify-between gap-2 py-0 hover:no-underline'
                  >
                    <div className='flex flex-col items-start'>
                      <span> {widget.name.split('-')[1]}</span>
                      <span className='text-xs font-normal text-muted-foreground'>
                        {selected.get(widget.sub_category)?.size}/
                        {widget.statistics.length}
                      </span>
                    </div>
                    <Icons.dropdown className='size-4 transition-transform duration-200' />
                  </AccordionTrigger>
                </div>
                <AccordionContent>
                  <div className='grid gap-2 p-2'>
                    {widget.statistics.map((stat) => (
                      <div
                        key={stat.identifier}
                        className='flex items-center gap-2 pl-2'
                      >
                        <Checkbox
                          id={stat.identifier}
                          checked={selected
                            .get(widget.sub_category)
                            ?.has(stat.identifier)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelected((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(
                                  widget.sub_category,
                                  new Set(
                                    prev
                                      .get(widget.sub_category)
                                      ?.add(stat.identifier)
                                  )
                                );
                                return newMap;
                              });
                            } else {
                              setSelected((prev) => {
                                prev
                                  .get(widget.sub_category)
                                  ?.delete(stat.identifier);
                                return new Map(prev);
                              });
                            }
                          }}
                        />
                        <Label htmlFor={stat.identifier}>{stat.name}</Label>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <DialogFooter>
          <Button onClick={onSubmit} type='submit'>
            Spara
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
